// ============================================================
// ClaimTypeFields.tsx — the REQUIRED claim type + the small secondary route
// select, shared by every "create a case" surface (NewCaseModal, the client
// page's "New case", DuplicateCaseDialog). B2.
// Picking a type PRE-FILLS its usual route (utils/claimTypes suggestedRoute);
// the route stays editable (and CICA has none). The server checks the type
// too (lib/caseClaim parseClaimCreate) — FieldError shows either refusal.
// Styles = the case-entry modals' shared classes (./caseEntryStyles).
// ============================================================
import React, { useEffect, useRef } from 'react'
import FieldError from '../shared/FieldError'
import { FIELD, FIELD_ERR, LBL, REQ } from './caseEntryStyles'
import {
  CLAIM_ROUTE_LABELS, CLAIM_TYPES, CLAIM_TYPE_LABELS, routeOnTypeChange, routesFor,
  type ClaimRoute, type ClaimType,
} from '../../utils/claimTypes'

export interface ClaimChoice { claimType: ClaimType | ''; claimRoute: ClaimRoute | '' }
export const EMPTY_CLAIM_CHOICE: ClaimChoice = { claimType: '', claimRoute: '' }
// The choice for a type just picked: its suggested route — (B3b) unless the
// route was chosen by hand (not empty, not the previous type's suggestion),
// which is kept when the new type allows it (utils/claimTypes routeOnTypeChange).
export const choiceForType = (claimType: ClaimType | '', prev: ClaimChoice = EMPTY_CLAIM_CHOICE): ClaimChoice => ({
  claimType,
  claimRoute: claimType ? routeOnTypeChange(prev.claimType || null, prev.claimRoute || null, claimType) ?? '' : '',
})
export const CLAIM_TYPE_MISSING = 'Choose a claim type.'

interface ClaimTypeFieldsProps {
  value: ClaimChoice
  onChange: (next: ClaimChoice) => void
  /** Shown under the claim type (a missing type, or the server's refusal). */
  error?: string | null
  idPrefix: string
  disabled?: boolean
}

const ClaimTypeFields: React.FC<ClaimTypeFieldsProps> = ({ value, onChange, error, idPrefix, disabled }) => {
  const typeId = `${idPrefix}-claim-type`
  const routeId = `${idPrefix}-claim-route`
  // (Audit L2) An error that appears (Create pressed without a type, or the
  // server refused it) is announced (role="alert") and focus moves to the
  // field — like the modal's other errors.
  const typeRef = useRef<HTMLSelectElement>(null)
  useEffect(() => { if (error) typeRef.current?.focus() }, [error])
  return (
    <div>
      <label className={LBL} htmlFor={typeId}>Claim type{REQ}</label>
      <select
        id={typeId}
        ref={typeRef}
        className={`${error ? FIELD_ERR : FIELD} ${value.claimType ? '' : 'text-casevo-text-soft'}`}
        value={value.claimType}
        disabled={disabled}
        required
        aria-required="true"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${typeId}-err` : undefined}
        onChange={(e) => onChange(choiceForType(e.target.value as ClaimType | '', value))}
      >
        <option value="">Choose…</option>
        {CLAIM_TYPES.map((t) => <option key={t} value={t} className="text-casevo-forest">{CLAIM_TYPE_LABELS[t]}</option>)}
      </select>
      <div id={`${typeId}-err`} role="alert"><FieldError message={error ?? undefined} /></div>
      <div className="flex items-center gap-2 mt-[6px]">
        <label htmlFor={routeId} className="text-[11.5px] font-medium text-casevo-text-soft shrink-0">Route</label>
        <select
          id={routeId}
          className="min-w-0 flex-1 min-h-[28px] border border-casevo-border rounded-md px-2 py-[4px] text-[12px] bg-casevo-input dark:bg-[#1f2e26] text-casevo-forest focus:outline-none focus:border-casevo-mid casevo-focus"
          value={value.claimRoute}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, claimRoute: e.target.value as ClaimRoute | '' })}
        >
          <option value="">None / not sure</option>
          {/* Only the routes that fit the claim type (utils/claimTypes ROUTES_BY_TYPE). */}
          {routesFor(value.claimType || null).map((r) => <option key={r} value={r}>{CLAIM_ROUTE_LABELS[r]}</option>)}
        </select>
      </div>
    </div>
  )
}

export default ClaimTypeFields
